import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  Alert,
  ScrollView,
} from "react-native";

import {
  MapPin,
  Building2,
  Navigation2,
  Home,
  Briefcase,
  Users,
  MapPinOff,
} from "lucide-react-native";
import HeaderBack from "../components/shared/component/HeaderBack";
import { registrationStyles } from "../components/shared/styles/registrationStyles";
import { FlickOColors } from "../components/shared/styles/color";
import { getResponsiveSize } from "../components/shared/styles/globalStyles";
import { RootStackParamList } from "../types/navigation";
import { NavigationProp, RouteProp } from "@react-navigation/native";
import { AddressDetails, UpdateUserReq, UserCreDB } from "../types/user";
import { userService } from "../services/user.service";
import LoadingSpinnerTrans from "../components/shared/LoadingSpinner";
import { generateId } from "../components/shared/utils/uuid";
import { secureStoreService } from "../components/shared/services/secure-store.service";
import { DecodedToken } from "../types/token";

interface SaveLocationType {
  icon: JSX.Element;
  label: string;
  id: "home" | "work" | "family" | "other";
}

interface FormData {
  flatNumber: string;
  buildingName: string;
  landmark: string;
  addressType: SaveLocationType["id"];
}

const saveLocationTypes: SaveLocationType[] = [
  {
    icon: <Home size={getResponsiveSize(24)} color={FlickOColors.primary} />,
    label: "Home",
    id: "home",
  },
  {
    icon: (
      <Briefcase size={getResponsiveSize(24)} color={FlickOColors.primary} />
    ),
    label: "Work",
    id: "work",
  },
  {
    icon: <Users size={getResponsiveSize(24)} color={FlickOColors.primary} />,
    label: "Friends & Family",
    id: "family",
  },
  {
    icon: (
      <MapPinOff size={getResponsiveSize(24)} color={FlickOColors.primary} />
    ),
    label: "Other",
    id: "other",
  },
];

interface Props {
  route: RouteProp<RootStackParamList, "AddressDetailsScreen">;
  navigation: NavigationProp<RootStackParamList>;
}

const AddressDetailsScreen = ({ route, navigation }: Props) => {
  const { locationDetails } = route.params;

  const [formData, setFormData] = useState<FormData>({
    flatNumber: "",
    buildingName: "",
    landmark: "",
    addressType: "other",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.flatNumber.trim()) {
      Alert.alert("Required", "Please enter flat/house number");
      return false;
    }
    if (!formData.buildingName.trim()) {
      Alert.alert("Required", "Please enter building/society name");
      return false;
    }
    if (!formData.addressType) {
      Alert.alert("Required", "Please select address type");
      return false;
    }
    return true;
  };

  const handleSaveAddress = async () => {
    try {
      const token: DecodedToken | null =
        await secureStoreService.getDecodedAccessToken();
      const user: UserCreDB | null = await userService.getCachedUser(
        token?.restUserId as string
      );

      // Check if we've reached address limit before other validations
      if (
        !user ||
        (user.location?.addresses?.length ?? 0) >= userService.getMaxAddresses()
      ) {
        Alert.alert("Error", "Maximum number of addresses reached");
        return false;
      }
      if (!validateForm()) return;

      setIsLoading(true);

      const newAddress: AddressDetails = {
        addressName: formData.addressType,
        locality: locationDetails.locality,
        city: locationDetails.city,
        coordinates: locationDetails.coordinates,
        flatNumber: formData.flatNumber,
        buildingName: formData.buildingName,
        landmark: formData.landmark || undefined,
      };

      // For now, simulating API call
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      const addressId = generateId();

      const currentAddresses = user?.location?.addresses?.[0] || {};
      const changes: UpdateUserReq = {
        location: {
          addresses: [
            {
              ...currentAddresses,
              [addressId]: newAddress,
            },
          ],
          currentId: addressId,
        },
      };
      await userService.updateUserData(token?.restUserId as string, changes); //updates in DB and API both
      navigation.navigate("HomeScreen");
    } catch (error) {
      Alert.alert("Error", "Failed to save address. Please try again.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={[registrationStyles.container, styles.container]}>
      {isLoading && <LoadingSpinnerTrans />}
      <HeaderBack title="Complete Address Details" />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Location Header */}
        <View style={styles.locationHeader}>
          <MapPin size={getResponsiveSize(24)} color={FlickOColors.primary} />
          <Text style={styles.locationText}>{locationDetails.locality}</Text>
        </View>

        <Text style={styles.fullAddress}>{locationDetails.fullAddress}</Text>

        {/* Help Text */}
        <View style={styles.helpTextContainer}>
          <Navigation2
            size={getResponsiveSize(20)}
            color={FlickOColors.success}
          />
          <Text style={styles.helpText}>
            A detailed address will help our Delivery Partner reach your
            doorstep easily
          </Text>
        </View>

        {/* Address Input Fields */}
        <View style={styles.inputSection}>
          <View style={styles.inputWrapper}>
            <Building2
              size={getResponsiveSize(20)}
              color={FlickOColors.text.secondary}
            />
            <TextInput
              style={styles.input}
              placeholder="Flat / House / Floor No."
              placeholderTextColor={FlickOColors.text.secondary}
              value={formData.flatNumber}
              onChangeText={(value) => handleInputChange("flatNumber", value)}
            />
          </View>

          <View style={styles.inputWrapper}>
            <MapPin
              size={getResponsiveSize(20)}
              color={FlickOColors.text.secondary}
            />
            <TextInput
              style={styles.input}
              placeholder="Building / Society / Apartment Name"
              placeholderTextColor={FlickOColors.text.secondary}
              value={formData.buildingName}
              onChangeText={(value) => handleInputChange("buildingName", value)}
            />
          </View>

          <View style={styles.inputWrapper}>
            <Navigation2
              size={getResponsiveSize(20)}
              color={FlickOColors.text.secondary}
            />
            <TextInput
              style={styles.input}
              placeholder="Nearby Landmark (Optional)"
              placeholderTextColor={FlickOColors.text.secondary}
              value={formData.landmark}
              onChangeText={(value) => handleInputChange("landmark", value)}
            />
          </View>
        </View>

        {/* Save As Section */}
        <Text style={styles.sectionTitle}>SAVE AS</Text>
        <View style={styles.saveTypesContainer}>
          {saveLocationTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={[
                styles.saveTypeButton,
                formData.addressType === type.id && styles.selectedSaveType,
              ]}
              onPress={() => handleInputChange("addressType", type.id)}
            >
              {type.icon}
              <Text
                style={[
                  styles.saveTypeText,
                  formData.addressType === type.id &&
                    styles.selectedSaveTypeText,
                ]}
              >
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {/* Submit Button */}
      <TouchableOpacity
        style={[
          registrationStyles.submitButton,
          styles.submitButton,
          isLoading && styles.disabledButton,
        ]}
        onPress={handleSaveAddress}
        disabled={isLoading}
      >
        <Text style={registrationStyles.submitButtonText}>
          {isLoading ? "SAVING ADDRESS..." : "SAVE ADDRESS"}
        </Text>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: FlickOColors.white,
  },
  content: {
    flex: 1,
    padding: getResponsiveSize(16),
  },
  locationHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: getResponsiveSize(4),
  },
  locationText: {
    fontSize: getResponsiveSize(24),
    fontWeight: "600",
    marginLeft: getResponsiveSize(8),
    color: FlickOColors.text.primary,
  },
  fullAddress: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(16),
  },
  helpTextContainer: {
    flexDirection: "row",
    backgroundColor: `${FlickOColors.success}15`,
    padding: getResponsiveSize(12),
    borderRadius: getResponsiveSize(8),
    marginBottom: getResponsiveSize(24),
    alignItems: "center",
  },
  helpText: {
    flex: 1,
    marginLeft: getResponsiveSize(8),
    fontSize: getResponsiveSize(14),
    color: FlickOColors.success,
  },
  inputSection: {
    gap: getResponsiveSize(16),
    marginBottom: getResponsiveSize(24),
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: FlickOColors.gray.light,
    borderRadius: getResponsiveSize(8),
    paddingHorizontal: getResponsiveSize(12),
    backgroundColor: FlickOColors.background.light,
  },
  input: {
    flex: 1,
    paddingVertical: getResponsiveSize(12),
    marginLeft: getResponsiveSize(8),
    fontSize: getResponsiveSize(16),
    color: FlickOColors.text.primary,
  },
  sectionTitle: {
    fontSize: getResponsiveSize(14),
    fontWeight: "600",
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(12),
  },
  saveTypesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: getResponsiveSize(12),
    marginBottom: getResponsiveSize(16),
  },
  saveTypeButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: getResponsiveSize(8),
    paddingHorizontal: getResponsiveSize(16),
    borderRadius: getResponsiveSize(24),
    borderWidth: 1,
    borderColor: FlickOColors.gray.light,
    backgroundColor: FlickOColors.white,
  },
  selectedSaveType: {
    backgroundColor: `${FlickOColors.primary}15`,
    borderColor: FlickOColors.primary,
  },
  saveTypeText: {
    marginLeft: getResponsiveSize(8),
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.primary,
  },
  selectedSaveTypeText: {
    color: FlickOColors.primary,
    fontWeight: "500",
  },
  submitButton: {
    margin: getResponsiveSize(16),
  },
  disabledButton: {
    opacity: 0.7,
  },
});

export default AddressDetailsScreen;
